import { createContext, ReactNode, useContext, useState } from "react";
import { useLocalStorage } from "./useLocalStorage";
import { AuthContextModel } from "./types/AuthContextModel";
import { UserAuthData } from "./types/UserAuthData";
import { UserForLoginDto } from "./types/UserForLoginDto";
import { Navigate, useLocation } from "react-router-dom";
import { configurationService } from "../../../../appConfigService";
import axios, { AxiosError } from "axios";
import { AuthenticationResultDto } from "./types/AuthenticationResultDto";
import { AuthStatus } from "./types/AuthStatus";
import { UserForRegistrationDto } from "./types/UserForRegistrationDto";
import dayjs from "dayjs";
import { BinanceAuthData } from "./types/BinanceAuthData";

const AuthContext = createContext<AuthContextModel | undefined>(undefined);

export const AuthProvider = ({
    children
}: {
    children?: ReactNode
}) => {
    const [userAuthData, setUserAuthData] = useLocalStorage<UserAuthData>("user", null);
    const [binanceAuthData, setBinanceUserAuthData] = useLocalStorage<BinanceAuthData>("binance", { apiKey: '', secretKey: '' });
    
    const location = useLocation();

    const login = async (userCredentials: UserForLoginDto): Promise<AuthStatus> => {
        let authStatus = AuthStatus.None;
        
        await axios
            .create({
                baseURL: configurationService.configuration.baseAddress,
            })
            .post<AuthenticationResultDto>(
                'api/authentication/login',
                userCredentials,
            )
            .then(response => {
                const result = response.data;
                setUserAuthData({
                    jwtToken: result.Token,
                    userName: result.UserName,
                    expiresInUtc: result.ExpiresInUtc
                });
                authStatus = AuthStatus.Successful;
            })
            .catch((error: AxiosError) => {
                authStatus = error?.response?.status === 401 
                    ? AuthStatus.Failed
                    : AuthStatus.ServerError;
            });

        return authStatus;
    };

    const registration = async (userCredentials: UserForRegistrationDto): Promise<AuthStatus> => {
        let authStatus = AuthStatus.None;

        await axios
            .create({
                baseURL: configurationService.configuration.baseAddress,
            })
            .post(
                'api/authentication/registration',
                userCredentials,
            )
            .then(response => {
                authStatus = AuthStatus.Successful;
            })
            .catch((error: AxiosError) => {
                authStatus = error?.response?.status === 400
                    ? AuthStatus.Failed
                    : AuthStatus.ServerError;
            });

        return authStatus;
    };
    
    const logout = (): void => {
        setUserAuthData(null);
    };
    
    const isUserAuthenticated = (): boolean =>
        userAuthData !== null &&
        (userAuthData.jwtToken !== null || userAuthData.jwtToken !== '') &&
        new Date(userAuthData.expiresInUtc) > dayjs().utc().toDate();
    
    const mapAnonymousRoute = (requestedRoute: string): string => {
        switch (requestedRoute) {
            case "/login":
                return requestedRoute;

            case "/signup":
                return requestedRoute;

            case "/forum":
                return requestedRoute;
                
            default:
                return "/login";
        }
    }
    
    return (
        <AuthContext.Provider
            value={{
                userAuthData,
                binanceAuthData,
                setBinanceUserAuthData,
                isUserAuthenticated,
                login,
                registration,
                logout
            }}
        >
            {!isUserAuthenticated()
                ? (
                    <>
                        <Navigate to={mapAnonymousRoute(location.pathname)} />
                        {children}
                    </>
                )
                : (
                    <>
                        <Navigate to={location} />
                        {children}
                    </>
                )
            }
        </AuthContext.Provider>
    );
};

export const useAuthContext = () => {
    const context = useContext(AuthContext);

    if (!context) {
        throw new Error('useAuthContext is undefined!');
    }

    return context;
}